<script>
import AppLoading2 from "@/components/elements/AppLoading2.vue";
import CmsModal2 from "@/components/elements/CmsModal2.vue";
import { mapActions, mapGetters } from "vuex";
import { paginationMixin } from "@/mixins/paginationMixin";
import { dataMixin } from "@/mixins/dataMixin";
import { gateMixin } from "@/mixins/gateMixin";
import { vueAppMixin } from "@/mixins/vueAppMixin";

export default {
  name: "FindCrewModal",
  components: { AppLoading2, CmsModal2 },
  mixins: [paginationMixin, dataMixin, gateMixin, vueAppMixin],
  props: ["title", "unique", "rank", "visibility"],
  emits: ["return-crew", "update_visibility"],

  data() {
    return {
      listStatus: "loading",
    };
  },

  computed: {
    ...mapGetters([
      "crews",
      "rankKeys",
      "crewSearchParam",
      "manningKeys",
      "natlKeys",
    ]),
  },

  methods: {
    ...mapActions([
      "getCrewList",
      "getRankKeys",
      "getManningKeys",
      "getNatlKeys",
      "clearCrewList"
    ]),
    async searchCrew(pageVal = null) {
      try {
        this.listStatus = "loading";

        let page = pageVal ?? this.pagination.currentPage;
        let params = {
          page: page,
          searchKey: this.crewSearchParam.searchKey,
          searchValue: this.crewSearchParam.searchValue,
          manning: this.crewSearchParam.manning,
          rank: this.crewSearchParam.rank,
          natl: this.crewSearchParam.natl,
          pooling: this.crewSearchParam.pooling,
          activityCode: this.crewSearchParam.activityCode,
        };
        if (this.crewStatus !== "") {
          params["crewStatus"] = this.crewSearchParam.crewStatus;
        }

        await this.getCrewList(params);
        this.listStatus = "filled";
      } catch (error) {
        console.error("Error fetching crew list:", error);
        this.listStatus = "error";
      }
    },
    async setPage(page = null) {
      this.pagination.currentPage = page ?? 1;
      await this.searchCrew();
    },
    selectCrew(crew) {
      this.$emit("return-crew", crew);
      this.$emit("update_visibility", false);
    },
  },
  async mounted() {
    try {
      await Promise.all([
        this.getRankKeys(),
        this.getManningKeys(),
        this.getNatlKeys(),
      ]);

      // Set the default selected on Load
      this.crewSearchParam.rank = this.rank;

      await this.searchCrew();
    } catch (error) {
      console.error("Error initializing component:", error);
      this.listStatus = "error";
    }
  },

  beforeDestroy() {
    this.clearCrewList();
  },
};
</script>

<template>
  <cms-modal2
    :title="title"
    :unique="unique"
    @closedModal="$emit('update_visibility', true)"
    size="xl"
  >
    <div>
      <div class="row mb-2">
        <div class="input-group col-12 col-lg-6 mb-2">
          <label
            for="crew_search"
            class="px-2 py-0 m-0 border-0 bg-white font-weight-bold input-group-text input-group-prepend"
            >Search Crew:</label
          >
          <input
            id="crew_search"
            class="form-control form-control-xs"
            v-model="crewSearchParam.searchValue"
            @keyup.enter="searchCrew(1)"
            type="text"
            placeholder="Enter Name"
            :disabled="listStatus === 'loading'"
          />
          <button
            type="button"
            class="btn input-group-append input-group-text"
            @click="searchCrew(1)"
            :disabled="listStatus === 'loading'"
          >
            <i class="fas fa-search"></i>
          </button>
        </div>

        <div class="input-group col-12 col-lg-6 mb-2">
          <label
            class="px-2 py-0 m-0 border-0 input-group-text font-weight-bold bg-white input-group-prepend form-label-xs"
            for="rank_id"
            >Rank:</label
          >
          <select
            id="rank_id"
            v-model="crewSearchParam.rank"
            @change="searchCrew(1)"
            class="form-control form-control-xs"
          >
            <!-- <option v-for="rank in rankKeys" :key="rank.id" :value="rank.id">{{
              rank.name
            }}</option> -->
            <template v-if="rank == 1">
              <option :value="[1, 3]">All Ranks</option>
              <option :key="1" :value="1">MASTER MARINER</option>
              <option :key="3" :value="3">CHIEF MATE</option>
            </template>

            <template v-else>
              <option :value="[2, 4]">All Ranks</option>
              <option :key="2" :value="2">CHIEF ENGINEER</option>
              <option :key="4" :value="4">FIRST ASSISTANT ENGINEER</option>
            </template>
          </select>
        </div>

        <div class="input-group col-12 col-lg-6 mb-2">
          <label
            class="px-2 py-0 m-0 border-0 input-group-text font-weight-bold bg-white input-group-prepend form-label-xs"
            for="manning_id"
            >Manning:</label
          >
          <select
            id="manning_id"
            v-model="crewSearchParam.manning"
            @change="searchCrew(1)"
            class="form-control form-control-xs"
            :disabled="listStatus === 'loading'"
          >
            <option :value="0">All Mannings</option>
            <option
              v-for="manning in manningKeys"
              :key="manning.id"
              :value="manning.id"
              >{{ manning.alias }}</option
            >
          </select>
        </div>
        <div class="input-group col-12 col-lg-6 mb-2">
          <label
            class="px-2 py-0 m-0 border-0 input-group-text font-weight-bold bg-white input-group-prepend form-label-xs"
            for="natl_id"
            >Nationality:</label
          >
          <select
            id="natl_id"
            v-model="crewSearchParam.natl"
            @change="searchCrew(1)"
            class="form-control form-control-xs"
            :disabled="listStatus === 'loading'"
          >
            <option :value="0">All Nationalities</option>
            <option
              v-for="natlKey in natlKeys"
              :key="natlKey.id"
              :value="natlKey.id"
              :title="natlKey.name"
            >
              {{ natlKey.natl }}
            </option>
          </select>
        </div>
      </div>

      <table class="table table-sm">
        <thead>
          <tr>
            <th>#</th>
            <th>RANK</th>
            <th>CREW IPN</th>
            <th>FULL NAME</th>
            <th>ACTIVITY CODE</th>
            <th>STATUS</th>
            <th>VESSEL/OFFICE</th>
            <th>NATIONALITY</th>
            <th>MANNING</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="listStatus === 'loading'">
            <td colspan="10" class="text-center py-4">
              <app-loading2 :loading-status="true"></app-loading2>
              <div class="mt-2">Loading crew list...</div>
            </td>
          </tr>
          <tr v-else-if="listStatus === 'error'">
            <td colspan="10" class="text-center py-4 text-danger">
              <i class="fas fa-exclamation-triangle"></i>
              <div class="mt-2">Error loading crew list. Please try again.</div>
            </td>
          </tr>
          <tr v-else-if="!crews || crews.length === 0">
            <td colspan="10" class="text-center py-4 text-muted">
              <i class="fas fa-users"></i>
              <div class="mt-2">No crew members found.</div>
            </td>
          </tr>
          <tr v-else v-for="(crew, index) in crews" :key="crew.id">
            <td>{{ index + pagination.from }}</td>
            <td :title="crew.rankName">{{ crew.rankAlias }}</td>
            <td v-if="crew.ipn">{{ crew.ipn }}</td>
            <td v-else>
              <span class="bg-lightblue" title="TEMPORARY CREW CODE">
                {{ crew.code }}
              </span>
            </td>
            <td>{{ crew.fullName }}</td>
            <td :title="crew.activityCodeName">{{ crew.activityCode }}</td>
            <td>{{ crew.active | status }}</td>
            <td>
              <span v-if="crew.vessel"> {{ crew.vessel }} </span>
              <span v-else>
                {{ crew.companyOfficeName }}
                <template
                  v-if="crew.companyOfficeName && crew.assignmentPositionName"
                  >/</template
                >
                {{ crew.assignmentPositionName }}
              </span>
            </td>
            <td>{{ crew.nationality }}</td>
            <td>{{ crew.manningAlias }}</td>
            <td>
              <button
                @click="selectCrew(crew)"
                class="btn btn-sm btn-primary"
                data-dismiss="modal"
              >
                Select
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <nav
      aria-label="..."
      v-if="listStatus !== 'loading' && crews && crews.length > 0"
    >
      <ul class="pagination text-smallest">
        <li
          class="page-item"
          :class="{ disabled: pagination.currentPage === 1 }"
        >
          <button
            class="page-link"
            @click="setPage(1)"
            :disabled="listStatus === 'loading'"
          >
            First
          </button>
        </li>

        <li
          v-for="page in paginationPages"
          :key="page"
          class="page-item"
          :class="{
            disabled: page === '...' || listStatus === 'loading',
            active: page === pagination.currentPage,
          }"
        >
          <button
            class="page-link"
            @click="setPage(page)"
            :disabled="listStatus === 'loading'"
          >
            {{ page }}
          </button>
        </li>

        <li
          class="page-item"
          :class="{
            disabled:
              pagination.currentPage === pagination.lastPage ||
              listStatus === 'loading',
          }"
        >
          <button
            class="page-link"
            @click="setPage(pagination.lastPage)"
            :disabled="listStatus === 'loading'"
          >
            Last
          </button>
        </li>
      </ul>
    </nav>
  </cms-modal2>
</template>

<style scoped></style>
